import {
  Activity,
  AreaChart,
  BarChart3,
  BarChartHorizontal,
  CalendarDays,
  Code2,
  Filter,
  Funnel,
  Gauge,
  Grid3x3,
  Hash,
  LayoutList,
  LineChart,
  ListChecks,
  PieChart,
  Rows3,
  ScatterChart,
  Search,
  Table2,
  Target,
  TextCursorInput,
  TrendingUp,
  Type,
  Waves,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";

export type Category = "Numbers" | "Charts" | "Tables" | "Filters" | "Content";

export interface VisualSpec {
  type: string;
  label: string;
  description: string;
  icon: LucideIcon;
  category: Category;
  size: { w: number; h: number };
  /** Which field wells this visual uses. */
  wells: { axis?: string; values?: string; legend?: boolean; maxValues?: number; field?: string };
  /** Visuals in the same group can be switched between without losing data bindings. */
  group?: "category-chart" | "part-to-whole" | "table" | "number" | "filter";
  needsData: boolean;
}

export const VISUALS: VisualSpec[] = [
  { type: "kpi", label: "KPI card", description: "One headline number", icon: Hash, category: "Numbers", size: { w: 6, h: 5 }, wells: { values: "Value", maxValues: 1 }, group: "number", needsData: true },
  { type: "gauge_chart", label: "Gauge", description: "One number against a target or range", icon: Gauge, category: "Numbers", size: { w: 6, h: 9 }, wells: { values: "Value", maxValues: 1 }, group: "number", needsData: true },
  { type: "kpi_trend", label: "KPI + trend", description: "Headline number, sparkline and change vs last period", icon: Activity, category: "Numbers", size: { w: 6, h: 7 }, wells: { axis: "Period (month or date)", values: "Value", maxValues: 1 }, group: "number", needsData: true },
  { type: "bar_chart", label: "Column / bar", description: "Compare values across categories", icon: BarChart3, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "X axis (category)", values: "Values", legend: true }, group: "category-chart", needsData: true },
  { type: "line_chart", label: "Line", description: "Trend over time", icon: LineChart, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "X axis (usually a date or month)", values: "Values", legend: true }, group: "category-chart", needsData: true },
  { type: "area_chart", label: "Area", description: "Trend with volume", icon: AreaChart, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "X axis", values: "Values", legend: true }, group: "category-chart", needsData: true },
  { type: "combo_chart", label: "Column + line", description: "Two measures, e.g. budget vs actual", icon: TrendingUp, category: "Charts", size: { w: 14, h: 12 }, wells: { axis: "X axis", values: "Values (first = columns)", legend: true }, group: "category-chart", needsData: true },
  { type: "bullet_chart", label: "Bullet · vs target", description: "Actual against target on each row", icon: Target, category: "Charts", size: { w: 12, h: 10 }, wells: { axis: "Category", values: "Actual, then target", maxValues: 2 }, group: "category-chart", needsData: true },
  { type: "scatter_chart", label: "Scatter / bubble", description: "How two numbers relate (third = bubble size)", icon: ScatterChart, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "One dot per", values: "X, then Y, then size", maxValues: 3 }, group: "category-chart", needsData: true },
  { type: "heatmap_chart", label: "Heatmap", description: "A grid coloured by value, e.g. vessel × month", icon: LayoutGrid, category: "Charts", size: { w: 14, h: 12 }, wells: { axis: "Columns, then rows", values: "Value", maxValues: 1 }, group: "category-chart", needsData: true },
  { type: "waterfall_chart", label: "Waterfall", description: "How a total is built up", icon: Waves, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "Category", values: "Value", maxValues: 1 }, group: "category-chart", needsData: true },
  { type: "donut_chart", label: "Donut", description: "Share of a total", icon: PieChart, category: "Charts", size: { w: 8, h: 12 }, wells: { axis: "Slices", values: "Value", maxValues: 1 }, group: "part-to-whole", needsData: true },
  { type: "pie_chart", label: "Pie", description: "Share of a total", icon: PieChart, category: "Charts", size: { w: 8, h: 12 }, wells: { axis: "Slices", values: "Value", maxValues: 1 }, group: "part-to-whole", needsData: true },
  { type: "treemap_chart", label: "Treemap", description: "Share with many categories", icon: Grid3x3, category: "Charts", size: { w: 12, h: 12 }, wells: { axis: "Groups", values: "Size", maxValues: 1 }, group: "part-to-whole", needsData: true },
  { type: "funnel_chart", label: "Funnel", description: "Stages that narrow, e.g. leads to deals", icon: Funnel, category: "Charts", size: { w: 8, h: 12 }, wells: { axis: "Stages", values: "Value", maxValues: 1 }, group: "part-to-whole", needsData: true },
  { type: "table", label: "Table", description: "Rows and columns of detail", icon: Table2, category: "Tables", size: { w: 24, h: 12 }, wells: { axis: "Columns", values: "Totals (optional)" }, group: "table", needsData: true },
  { type: "matrix", label: "Matrix", description: "Cross-tab, like a pivot table", icon: Rows3, category: "Tables", size: { w: 24, h: 12 }, wells: { axis: "Rows / columns", values: "Values" }, group: "table", needsData: true },
  { type: "grouped_table", label: "Grouped table", description: "Rows grouped with subtotals", icon: LayoutList, category: "Tables", size: { w: 24, h: 12 }, wells: { axis: "Group by, then columns", values: "Values" }, group: "table", needsData: true },
  { type: "status_list", label: "Status list", description: "Items with a coloured status", icon: ListChecks, category: "Tables", size: { w: 8, h: 12 }, wells: { axis: "Item + status", values: "Value (optional)" }, needsData: true },
  { type: "filter", label: "Dropdown filter", description: "Let viewers pick a value", icon: Filter, category: "Filters", size: { w: 4, h: 3 }, wells: { field: "Filter on field" }, group: "filter", needsData: true },
  { type: "date_filter", label: "Date filter", description: "Pick a date or period", icon: CalendarDays, category: "Filters", size: { w: 4, h: 3 }, wells: { field: "Date field" }, group: "filter", needsData: true },
  { type: "text_filter", label: "Search box", description: "Free-text search over a field", icon: Search, category: "Filters", size: { w: 5, h: 3 }, wells: { field: "Search in field" }, group: "filter", needsData: true },
  { type: "text", label: "Text", description: "Titles, notes, captions", icon: Type, category: "Content", size: { w: 8, h: 3 }, wells: {}, needsData: false },
  { type: "html", label: "HTML/CSS panel", description: "Custom design, e.g. built by an AI", icon: Code2, category: "Content", size: { w: 12, h: 8 }, wells: { axis: "Fields for {{placeholders}}", values: "Measures" }, needsData: false },
];

export const GAUGE_ICON: LucideIcon = Gauge;
export const HORIZONTAL_BAR_ICON: LucideIcon = BarChartHorizontal;
export const TEXT_INPUT_ICON: LucideIcon = TextCursorInput;

export const CATEGORIES: Category[] = ["Numbers", "Charts", "Tables", "Filters", "Content"];

export function specFor(type: string): VisualSpec | undefined {
  return VISUALS.find((v) => v.type === type);
}

export function switchableTypes(type: string): VisualSpec[] {
  const s = specFor(type);
  if (!s?.group) return [];
  return VISUALS.filter((v) => v.group === s.group && v.type !== type);
}

/** Number formats the renderer understands (display.format, contract v2.1 section 2). */
export const NUMBER_FORMATS: Array<{ value: string; label: string; example: string }> = [
  { value: "", label: "Automatic", example: "picked from the data" },
  { value: "number", label: "Number", example: "1,234,567" },
  { value: "integer", label: "Whole number", example: "1,234,568" },
  { value: "decimal1", label: "1 decimal place", example: "1,234.6" },
  { value: "decimal2", label: "2 decimal places", example: "1,234.57" },
  { value: "compact", label: "Short (K / M)", example: "1.2M" },
  { value: "percent", label: "Percent", example: "12%" },
  { value: "percent1", label: "Percent, 1 decimal", example: "12.3%" },
  { value: "usd", label: "US dollars", example: "$1,234,567" },
  { value: "usd_compact", label: "US dollars, short", example: "$1.2M" },
  { value: "egp", label: "Egyptian pounds", example: "EGP 1,234,567" },
  { value: "egp_compact", label: "Egyptian pounds, short", example: "EGP 1.2M" },
];

export const PALETTES: Array<{ id: string; label: string; colors: string[] }> = [
  { id: "maridive", label: "Look default", colors: ["#3b82f6", "#22c55e", "#eab308", "#ef4444", "#a855f7", "#06b6d4", "#f97316"] },
  { id: "ocean", label: "Ocean", colors: ["#0ea5e9", "#22d3ee", "#14b8a6", "#6366f1", "#8b5cf6", "#38bdf8", "#2dd4bf"] },
  { id: "executive", label: "Executive", colors: ["#4dd8ff", "#2dd4bf", "#fbbf24", "#f472b6", "#a78bfa", "#60a5fa", "#34d399"] },
  { id: "traffic", label: "Status", colors: ["#22c55e", "#eab308", "#ef4444", "#94a3b8", "#3b82f6", "#a855f7", "#f97316"] },
  { id: "mono", label: "Mono blue", colors: ["#1d4ed8", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#1e40af"] },
  { id: "earth", label: "Earth", colors: ["#b45309", "#65a30d", "#0f766e", "#a16207", "#9f1239", "#475569", "#ca8a04"] },
];

/** Swatches offered in every colour picker: brand, status, neutrals. */
export const SWATCHES: string[] = [
  "#071d3b", "#0f6cbd", "#18a8c9", "#12a89d", "#4dd8ff", "#3b82f6", "#6366f1", "#a855f7",
  "#22c55e", "#eab308", "#d9911a", "#f97316", "#ef4444", "#ec4899", "#fbbf24", "#94a3b8",
  "#ffffff", "#f1f5f9", "#cbd5e1", "#64748b", "#475569", "#1e293b", "#0b1d34", "#000000",
];

/* ---- Chart-type gallery (contract v2.1 "Chart-switcher groups") ----
 * A variant = widget type + the display.chart keys that make it look that
 * way. Switching keeps every binding the new type can use. */
export type SwitchGroup = "category-chart" | "part-to-whole" | "number";
export interface ChartVariant {
  id: string;
  label: string;
  type: string;
  group: SwitchGroup;
  /** display.chart keys this variant sets; `undefined` values are removed. */
  chart: Record<string, string | undefined>;
  hint: string;
}

export const SWITCH_GROUP_LABEL: Record<SwitchGroup, string> = {
  "category-chart": "Compare & trend",
  "part-to-whole": "Share of a total",
  number: "Single number",
};

export const CHART_VARIANTS: ChartVariant[] = [
  { id: "column", label: "Column", type: "bar_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "none" }, hint: "Compare categories side by side" },
  { id: "column-stacked", label: "Stacked column", type: "bar_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "stacked" }, hint: "Totals, and what they are made of" },
  { id: "column-100", label: "100% column", type: "bar_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "100_percent_stacked" }, hint: "The mix as a % of each total" },
  { id: "bar", label: "Bar", type: "bar_chart", group: "category-chart", chart: { orientation: "horizontal", stackMode: "none" }, hint: "Long names, ranked lists" },
  { id: "bar-stacked", label: "Stacked bar", type: "bar_chart", group: "category-chart", chart: { orientation: "horizontal", stackMode: "stacked" }, hint: "Ranked totals with their parts" },
  { id: "bar-100", label: "100% bar", type: "bar_chart", group: "category-chart", chart: { orientation: "horizontal", stackMode: "100_percent_stacked" }, hint: "The mix as a % per row" },
  { id: "line", label: "Line", type: "line_chart", group: "category-chart", chart: { orientation: undefined, stackMode: undefined }, hint: "Trend over time" },
  { id: "area", label: "Area", type: "area_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "none" }, hint: "Trend with a sense of volume" },
  { id: "area-stacked", label: "Stacked area", type: "area_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "stacked" }, hint: "How parts add up over time" },
  { id: "area-100", label: "100% area", type: "area_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "100_percent_stacked" }, hint: "How the mix shifts over time" },
  { id: "combo", label: "Column + line", type: "combo_chart", group: "category-chart", chart: { orientation: undefined, stackMode: "none" }, hint: "Two measures, e.g. budget vs actual" },
  { id: "waterfall", label: "Waterfall", type: "waterfall_chart", group: "category-chart", chart: { orientation: undefined, stackMode: undefined }, hint: "How a total is built up" },
  { id: "bullet", label: "Bullet", type: "bullet_chart", group: "category-chart", chart: { orientation: undefined, stackMode: undefined }, hint: "Actual vs target, row by row" },
  { id: "scatter", label: "Scatter", type: "scatter_chart", group: "category-chart", chart: { orientation: undefined, stackMode: undefined }, hint: "Two numbers against each other" },
  { id: "heatmap", label: "Heatmap", type: "heatmap_chart", group: "category-chart", chart: { orientation: undefined, stackMode: undefined }, hint: "Two categories, coloured by value" },
  { id: "donut", label: "Donut", type: "donut_chart", group: "part-to-whole", chart: { orientation: undefined, stackMode: undefined }, hint: "Share of a total, few slices" },
  { id: "pie", label: "Pie", type: "pie_chart", group: "part-to-whole", chart: { orientation: undefined, stackMode: undefined }, hint: "Share of a total, few slices" },
  { id: "treemap", label: "Treemap", type: "treemap_chart", group: "part-to-whole", chart: { orientation: undefined, stackMode: undefined }, hint: "Share across many categories" },
  { id: "funnel", label: "Funnel", type: "funnel_chart", group: "part-to-whole", chart: { orientation: undefined, stackMode: undefined }, hint: "Stages that narrow down" },
  { id: "kpi", label: "KPI card", type: "kpi", group: "number", chart: { orientation: undefined, stackMode: undefined }, hint: "One headline number" },
  { id: "gauge", label: "Gauge", type: "gauge_chart", group: "number", chart: { orientation: undefined, stackMode: undefined }, hint: "A number against a target" },
  { id: "kpi-trend", label: "KPI + trend", type: "kpi_trend", group: "number", chart: { orientation: undefined, stackMode: undefined }, hint: "Number, sparkline and change" },
];

/** Which gallery tile the widget currently matches. */
export function currentVariant(type: string, chart: Record<string, unknown> | undefined): ChartVariant | undefined {
  const c = chart ?? {};
  const horizontal = c.orientation === "horizontal";
  const stack = (c.stackMode as string) || "none";
  const candidates = CHART_VARIANTS.filter((v) => v.type === type);
  if (candidates.length <= 1) return candidates[0];
  return (
    candidates.find((v) => (v.chart.orientation === "horizontal") === horizontal && (v.chart.stackMode ?? "none") === stack) ??
    candidates.find((v) => (v.chart.orientation === "horizontal") === horizontal) ??
    candidates[0]
  );
}

export function variantGroupOf(type: string): SwitchGroup | undefined {
  return CHART_VARIANTS.find((v) => v.type === type)?.group;
}

/* ---- Looks (definition.theme, contract v2.1 section 3) ---- */
export interface LookTokens { stage: string; card: string; border: string; radius: number; shadow: string; title: string; text: string; accent: string[]; titleWeight: number; upper?: boolean }
export interface LookSpec {
  id: string;
  label: string;
  bestFor: string;
  /** Mini-preview tokens per mode -- an approximation of the renderer's Look CSS. */
  preview: Record<"light" | "dark", LookTokens>;
}

export const LOOKS: LookSpec[] = [
  {
    id: "maridive",
    label: "Maridive",
    bestFor: "Everyday company dashboards in the Maridive navy-and-cyan brand.",
    preview: {
      light: { stage: "#eef2f7", card: "#ffffff", border: "#d6dee8", radius: 5, shadow: "0 1px 2px rgba(7,29,59,.10)", title: "#071d3b", text: "#58657a", accent: ["#18a8c9", "#0f6cbd", "#12a89d"], titleWeight: 700 },
      dark: { stage: "#050d1a", card: "#0b1d34", border: "#1f3a5a", radius: 5, shadow: "none", title: "#e6f1fb", text: "#8fa6bf", accent: ["#4dd8ff", "#3b82f6", "#2dd4bf"], titleWeight: 700 },
    },
  },
  {
    id: "executive",
    label: "Executive",
    bestFor: "Boardroom screens: big numbers, calm colours, lots of breathing room.",
    preview: {
      light: { stage: "#f5f6f8", card: "#ffffff", border: "#e5e7eb", radius: 8, shadow: "0 2px 6px rgba(15,23,42,.07)", title: "#111827", text: "#6b7280", accent: ["#1e3a8a", "#94a3b8", "#0ea5e9"], titleWeight: 600 },
      dark: { stage: "#0b1220", card: "#111a2e", border: "#1e2a44", radius: 8, shadow: "none", title: "#f8fafc", text: "#94a3b8", accent: ["#93c5fd", "#cbd5e1", "#38bdf8"], titleWeight: 600 },
    },
  },
  {
    id: "premium",
    label: "Premium",
    bestFor: "Showcase reports for visitors: rich cards, soft depth, gold accents.",
    preview: {
      light: { stage: "#f4f1ea", card: "#fffdf8", border: "#e7dcc3", radius: 11, shadow: "0 3px 10px rgba(120,90,30,.16)", title: "#2b2112", text: "#7a6a4f", accent: ["#c8962c", "#1f3b5c", "#8a6d3b"], titleWeight: 700 },
      dark: { stage: "#0d0b08", card: "#1a1610", border: "#3a2f1c", radius: 11, shadow: "0 3px 12px rgba(0,0,0,.55)", title: "#f5e6c4", text: "#b8a582", accent: ["#e5b95c", "#7aa2d6", "#c49a52"], titleWeight: 700 },
    },
  },
  {
    id: "minimal",
    label: "Minimal",
    bestFor: "Dense operations pages: flat, no borders, the data does the talking.",
    preview: {
      light: { stage: "#ffffff", card: "#f8fafc", border: "transparent", radius: 0, shadow: "none", title: "#334155", text: "#94a3b8", accent: ["#475569", "#94a3b8", "#0ea5e9"], titleWeight: 600, upper: true },
      dark: { stage: "#0a0f16", card: "#0f1620", border: "transparent", radius: 0, shadow: "none", title: "#cbd5e1", text: "#64748b", accent: ["#cbd5e1", "#64748b", "#38bdf8"], titleWeight: 600, upper: true },
    },
  },
  {
    id: "contrast",
    label: "High contrast",
    bestFor: "Screens seen from far away or in bright rooms: bold colour, heavy text.",
    preview: {
      light: { stage: "#ffffff", card: "#ffffff", border: "#000000", radius: 3, shadow: "none", title: "#000000", text: "#1f2937", accent: ["#0047ff", "#e00000", "#008a00"], titleWeight: 800 },
      dark: { stage: "#000000", card: "#000000", border: "#ffffff", radius: 3, shadow: "none", title: "#ffffff", text: "#f3f4f6", accent: ["#ffd400", "#00e5ff", "#ff4fd8"], titleWeight: 800 },
    },
  },
];

/** Old reports use "maridive-dark" -- the same Look as "maridive". */
export function lookIdOf(theme: string | undefined | null): string {
  if (!theme || theme === "maridive-dark") return "maridive";
  return LOOKS.some((l) => l.id === theme) ? theme : "maridive";
}

/** Kept for older imports. */
export const THEMES: Array<{ id: string; label: string }> = LOOKS.map((l) => ({ id: l.id, label: l.label }));
